import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable} from "mobx";
import {SocialNetwork} from "data/enums";
import {share} from "data/utils";
import type {IShareImage, IShareStore} from "data/stores/share/share.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";

export interface IShareController extends ViewController {
	get i18n(): ILocalizationStore;
	get isOpenShareModal(): boolean;
	get shareImagePath(): IShareImage;
	get isMobileShareVisible(): boolean;

	shareViaFB: () => void;
	shareViaTW: () => void;
	shareViaMobile: () => void;

	closeShareModal: () => void;
}

@injectable()
export class ShareController implements IShareController {
	@observable private _isNarrowScreen: boolean = false;

	get i18n() {
		return this._i18nStore;
	}

	get isOpenShareModal() {
		return this._shareStore.isOpenShareModal;
	}

	get shareImagePath() {
		return this._shareStore.shareImagePath;
	}

	get isMobileShareVisible() {
		return "share" in navigator && this._isNarrowScreen;
	}

	private get narrowScreenWatcher() {
		return window.matchMedia("(max-width: 960px)");
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ShareStore) public _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	private share(socialNetwork: SocialNetwork) {
		share({
			socialNetwork,
			userId: this._userStore.user?.id || 0,
			type: this._shareStore.getShareType,
			message: this.i18n.t(
				"modal.share.message",
				"Here are my Ryder Cup team picks. Who would make your Ryder Cup team? Play Your Picks now!"
			),
		});
	}

	shareViaFB = () => this.share(SocialNetwork.Facebook);

	shareViaTW = () => this.share(SocialNetwork.Twitter);

	shareViaMobile = () => this.share(SocialNetwork.Mobile);

	closeShareModal = () => {
		this._shareStore.closeShareModal();
	};

	@action private updateNarrowScreenFlag = () => {
		this._isNarrowScreen = this.narrowScreenWatcher.matches;
	};

	dispose(): void {
		this.narrowScreenWatcher.removeEventListener("change", this.updateNarrowScreenFlag);
	}

	init(): void {
		this.updateNarrowScreenFlag();
		this.narrowScreenWatcher.addEventListener("change", this.updateNarrowScreenFlag);
	}
}
